import React, { useEffect, useRef, useState, useCallback, useMemo, useContext } from "react"
import { FallbackComponent } from '../../shared/components/FallbackComponent'
import { ErrorBoundary } from 'react-error-boundary'
import LookUpService from './LookUpService';
import { toast } from 'react-toastify';
import Logger from '../../shared/services/Logger';
import * as constant from '../../shared/util/constant'
import LookUpDetailModel from '../../Admin/model/LookUpDetailModel';
import Grid from '../../components/components/Grid/Grid';
import { useConfirmationModalContext } from '../../shared/components/ModalConfirmationContext';
import * as label from '../../shared/util/label'
import { Column, Icon, Label, Row, Select } from '../../components';
import { Button } from "react-bootstrap";
import './LookUpDetails.scss';
import AppContext from '../../shared/components/AppContext';

interface DetailsGridProps {
    tableId: string;
    data: LookUpDetailModel[];
    tableName: string;
}

const LookUpDetails: React.FunctionComponent<DetailsGridProps> = ({
    tableId,
    data,
    tableName
}) => {
    const [gridData, setLookupDetailsGrid] = useState(data);
    const modalContext = useConfirmationModalContext();
    const lookUpService = new LookUpService();
    const divToScroll = useRef(null);
    const [disableAddButton, setDisableAddButton] = useState(false);
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
    const [draftRows, setDraftRows] = React.useState<Record<string, Partial<LookUpDetailModel>>>({});
    const [savingRows, setSavingRows] = React.useState<Set<string>>(new Set());
    const gridWrapperRef = React.useRef<HTMLDivElement>(null);
    const [lastClickedField, setLastClickedField] = useState<keyof LookUpDetailModel | null>(null);
    const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
    const lookupContext = useContext(AppContext);    

    useEffect(() => {
        getLookUpDetail(tableId);
        setEditingRowId(null);
        setLastClickedField(null);
        setDisableSaveButton(true);
        setDisableAddButton(false);
        setDraftRows({});
        setErrors({});
        setSavingRows(new Set());
    }, [tableId]);

    useEffect(() => {
        const hasNewRow = (gridData ?? []).some(r => r.id === constant.DEFAULT_GUID);
        setDisableSaveButton(!hasNewRow);
    }, [gridData]);

    const getLookUpDetail = async (tableId: string) => {
        try {
            let data = await lookUpService.getLookUpDetail(tableId);
            if (data) {
                let response = data?.map((item) => {
                    return {
                        id: item.id,
                        tableId: item.tableId,
                        lookupCode: item.lookupCode ?? "",
                        lookupValue: item.lookupValue ?? "",
                        inactive: item.inactive ?? false,
                    }
                })
                response.sort((a, b) => (a.lookupCode || '').localeCompare(b.lookupCode || ''));
                setLookupDetailsGrid(response);
            }
        }
        catch (error) {
            toast.error(constant.ERROR_MESSAGE_LOAD, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            Logger.logError(error.toString());
        }
    }

    const deleteRow = async (lookUpId: string, lookupValue: string) => {
        if (lookupValue != "") {
            const result = await modalContext.showConfirmation(`Are you sure you want to delete the LookUp with Value: ${lookupValue}?`);
            if (result) {
                lookUpService.deleteLookUpDetail(lookUpId)
                    .then((response) => {
                        if (response) {
                            lookUpService.reloadLookupDetailsToCache(tableId);
                            toast.success("Look Up value successfully deleted", {
                                position: toast.POSITION.TOP_RIGHT,
                                autoClose: constant.AUTO_CLOSE
                            });
                            let selectedData = gridData.filter(item => item.id != lookUpId)
                            setLookupDetailsGrid([...selectedData]);
                        }
                        else {
                            toast.error("Unable to delete the look up value", {
                                position: toast.POSITION.TOP_RIGHT,
                                autoClose: constant.AUTO_CLOSE
                            });
                        }
                    })
                    .catch(e => {
                        console.log(e);
                        setDisableAddButton(false);
                    });
            }
        }
    }

    const isNewRow = (original?: LookUpDetailModel) =>
        !!original && original.id === constant.DEFAULT_GUID;

    const onFieldChange = <K extends keyof LookUpDetailModel>(
        rowId: LookUpDetailModel["id"],
        field: K,
        value: LookUpDetailModel[K]
    ) => {
        lookupContext.formSavedStatus = false;
        setLookupDetailsGrid((prev) =>
            (prev ?? []).map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
        );

        const original = (gridData ?? []).find((r) => r.id === rowId);
        const originalVal = original?.[field];
        if (originalVal !== value) {
            setDisableSaveButton(false);
        }
    };

    const addNewRow = () => {
        if (editingRowId) return;

        setDisableAddButton(true);
        setDisableSaveButton(false);

        const newRow: LookUpDetailModel = {
            id: constant.DEFAULT_GUID,
            lookupCode: null,
            lookupValue: null,
            tableId: tableId,
            inactive: false,
        };

        setLookupDetailsGrid(gridData => {
            const newArray = [...gridData];
            newArray.unshift(newRow);
            return newArray;
        });

        setEditingRowId(constant.DEFAULT_GUID);
        setDraftRows((prev) => ({ ...prev, [constant.DEFAULT_GUID]: { ...newRow } }));
        setDisableSaveButton(true);
        setDisableAddButton(true);

    }

    const validateRow = useCallback((row: LookUpDetailModel): Record<string, string> => {
        const rowErrors: Record<string, string> = {};
        if (row.id !== constant.DEFAULT_GUID) {
            if ((!row.lookupValue || row.lookupValue.trim() === '') && (!row.lookupCode || row.lookupCode.trim() === '')) {
                rowErrors.lookupValue = 'LookUp Value or LookUp Code is required';
            }
        }
        return rowErrors;
    }, []);

    const isEmpty = (v: any) => v == null || (typeof v === 'string' && v.trim() === '');

    const isEmptyNewDraftRow = (row?: Partial<LookUpDetailModel>) => {
        if (!row) return false;
        const isZeroId = row.id === constant.DEFAULT_GUID;
        const codeEmpty = isEmpty(row.lookupCode);
        const valueEmpty = isEmpty(row.lookupValue);
        return isZeroId && codeEmpty && valueEmpty;
    };

    const saveRecord = async () => {
        setDisableSaveButton(true);
        const newRow = (gridData ?? []).find((r) => r.id === constant.DEFAULT_GUID);
        const isCreating = !!newRow;

        if (isCreating && newRow) {
            try {
                const payload = { ...newRow, id: crypto?.randomUUID?.() };

                const rowErrors = validateRow(payload);
                setErrors(prev => ({ ...prev, [newRow.id]: rowErrors }));
                if (Object.keys(rowErrors).length > 0) {
                    setDisableAddButton(true);
                    return;
                }

                setSavingRows((prev) => {
                    const next = new Set(prev);
                    next.add(newRow.id);
                    return next;
                });

                const response = await lookUpService.saveLookUpDetail(payload);
                await lookUpService.reloadLookupDetailsToCache(payload.tableId);

                if (response) {
                    lookupContext.formSavedStatus = true;
                    toast.success("Look Up value created successfully", {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: constant.AUTO_CLOSE,
                    });

                    setLookupDetailsGrid((prev) =>
                        (prev ?? []).map((r) => (r.id === constant.DEFAULT_GUID ? { ...r, id: payload.id } : r))
                    );

                    setDraftRows((prev) => {
                        const { [constant.DEFAULT_GUID]: _, ...rest } = prev;
                        return rest;
                    });

                    setErrors(prev => {
                        const next = { ...prev };
                        delete next[constant.DEFAULT_GUID];
                        return next;
                    });
                    setEditingRowId(null);
                    setDisableSaveButton(true);
                    setDisableAddButton(false);
                } else {
                    toast.error("Unable to create new look up value", {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: constant.AUTO_CLOSE,
                    });
                    setDisableSaveButton(false);
                }
            } catch (e) {
                console.error(e);
                setDisableSaveButton(false);
            } finally {
                setSavingRows((prev) => {
                    const p = new Set(prev);
                    p.delete(constant.DEFAULT_GUID);
                    return p;
                });
            }
            return;
        }

        if (editingRowId) {
            const rowId = editingRowId;
            const draft = draftRows[rowId] ?? {};
            const original = (gridData ?? []).find((r) => r.id === rowId);
            if (!original) {
                setDisableAddButton(false);
                return;
            }

            const updated: LookUpDetailModel = {
                ...original,
                ...draft,
            };

            try {
                setSavingRows((prev) => {
                    const next = new Set(prev);
                    next.add(rowId);
                    return next;
                });

                const response = await lookUpService.updateLookUpDetail(updated);
                await lookUpService.reloadLookupDetailsToCache(updated.tableId);

                if (response) {
                    lookupContext.formSavedStatus = true;
                    setLookupDetailsGrid((prev) => (prev ?? []).map((r) => (r.id === rowId ? updated : r)));

                    setDraftRows((prev) => {
                        const { [rowId]: _, ...rest } = prev;
                        return rest;
                    });

                    setErrors(prev => {
                        const next = { ...prev };
                        delete next[rowId];
                        return next;
                    });

                    toast.success("Lookup details updated successfully", {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: constant.AUTO_CLOSE,
                    });

                    exitEditRow();
                } else {
                    toast.error("Unable to update lookup details", {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: constant.AUTO_CLOSE,
                    });
                    setDisableSaveButton(false);
                }
            } catch (e) {
                console.error(e);
                setDisableSaveButton(false);
            } finally {
                setSavingRows((prev) => {
                    const p = new Set(prev);
                    p.delete(rowId);
                    return p;
                });
                setDisableAddButton(false);
            }
        } else {
            setDisableAddButton(false);
        }
    };

    const enterEditRow = (rowId: LookUpDetailModel["id"], field?: keyof LookUpDetailModel) => {
        setEditingRowId(prev => {
            if (prev && prev !== rowId) return prev;
            return rowId;
        });
        setLastClickedField(field ?? null);
        setDisableAddButton(true);
    };

    const exitEditRow = () => {
        setEditingRowId(null);

        const hasDrafts = Object.keys(draftRows).length > 0;
        setDisableSaveButton(!hasDrafts);
        setDisableAddButton(hasDrafts);
    }

    const updateDraft = (rowId: string, field: keyof LookUpDetailModel, value: any) => {
        setDraftRows((prev) => {
            const nextRowDraft = { ...(prev[rowId] ?? {}), [field]: value };
            const next = { ...prev, [rowId]: nextRowDraft };
            return next;
        });
        lookupContext.formSavedStatus = false;
        const original = (gridData ?? []).find((r) => r.id === rowId);
        const originalVal = original ? (original as any)[field] : undefined;
        if (originalVal !== value) {
            setDisableSaveButton(false);
        }
    };

    const cancelEdit = (rowId: string) => {
        const currentDraft = draftRows?.[rowId];

        if (isEmptyNewDraftRow(currentDraft)) {
            setDraftRows((prev) => {
                const { [rowId]: _, ...rest } = prev;
                return rest;
            });

            setLookupDetailsGrid((prev) => prev.filter((r) => r.id !== constant.DEFAULT_GUID));
            setDisableSaveButton(true);
            setDisableAddButton(false);
            setEditingRowId(null);
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!editingRowId) return;
            const container = gridWrapperRef.current;

            if (!container) return;

            if (!container.contains(e.target as Node)) {
                cancelEdit(editingRowId);
                exitEditRow();
            }
        };

        document.addEventListener("mousedown", handler, true);
        return () => document.removeEventListener("mousedown", handler, true);
    }, [editingRowId, draftRows]);


    React.useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                cancelEdit(editingRowId);
            }
        };

        window.addEventListener('keydown', onKeyDown, { capture: true });
        return () => window.removeEventListener('keydown', onKeyDown as any, { capture: true } as any);
    }, [editingRowId, draftRows]);


    const columns: any[] = [
        {
            Header: "",
            accessor: "delete",
            Cell: (cell: { row?: { original?: LookUpDetailModel } }) => {
                const original = cell.row?.original;
                if (!original) return null;

                const rowId = original.id;
                const isRowEditing = editingRowId === rowId;
                const isSaving = savingRows.has(rowId);
                const disableDelete =
                    isNewRow(original) || isRowEditing || isSaving || disableAddButton;

                return (
                    <div
                        className={`delete-container ${disableDelete ? "disabled" : ""}`}
                    >
                        <Icon
                            icon={"trash"}
                            className={`grid-icon${disableDelete ? " disabled" : ""}`}
                            aria-disabled={disableDelete}
                            onClick={() => {
                                if (disableDelete) return;
                                deleteRow(original.id, original.lookupValue);
                            }}
                        />
                    </div>
                );
            },
            width: 100,
            cellClassName: "",
            headerClassName: ""
        },
        {
            Header: label.LookUpTableLabels.LOOKUP_VALUE,
            accessor: "lookupValue",
            Cell: (cell: { row?: { original?: LookUpDetailModel } }) => {
                const original = cell.row?.original;
                if (!original) return null;

                const rowId = original.id;
                const isRowEditing = editingRowId === rowId;
                const isSaving = savingRows.has(rowId);
                const value = (isRowEditing || isNewRow(original))
                    ? (draftRows[rowId]?.lookupValue ?? original.lookupValue ?? "")
                    : (original.lookupValue ?? "");
                const shouldAutoFocus = isRowEditing && lastClickedField === "lookupValue";
                const fieldError = errors[rowId]?.lookupValue;

                if (isNewRow(original) || isRowEditing) {
                    return (
                        <div className="cell-edit">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={value}
                                disabled={isSaving}
                                placeholder={label.LookUpTableLabels.LOOKUP_VALUE}
                                autoFocus={shouldAutoFocus}
                                onFocus={() => setLastClickedField("lookupValue")}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    updateDraft(rowId, "lookupValue", v);
                                    if (isNewRow(original)) {
                                        onFieldChange(rowId, "lookupValue", v);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") setDisableSaveButton(false);
                                    if (e.key === "Escape") {
                                        setLastClickedField(null);
                                        cancelEdit(rowId);
                                    }
                                }}
                            />
                            {fieldError && <small className="text-danger">{fieldError}</small>}
                        </div>
                    );
                }

                return (
                    <span
                        className="cell-readonly"
                        title="Hover or click to edit"
                        role="button"
                        tabIndex={0}
                        onMouseDown={() => enterEditRow(rowId, "lookupValue")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") enterEditRow(rowId, "lookupValue");
                        }}
                    >
                        {value}
                    </span>
                );
            },
            width: 300,
        },
        {
            Header: label.LookUpTableLabels.LOOKUP_CODE,
            accessor: "lookupCode",
            Cell: (cell: { row?: { original?: LookUpDetailModel } }) => {
                const original = cell.row?.original;
                if (!original) return null;

                const rowId = original.id;
                const isRowEditing = editingRowId === rowId;
                const isSaving = savingRows.has(rowId);
                const value = (isRowEditing || isNewRow(original))
                    ? (draftRows[rowId]?.lookupCode ?? original.lookupCode ?? "")
                    : (original.lookupCode ?? "");
                const shouldAutoFocus = isRowEditing && lastClickedField === "lookupCode";
                const fieldError = errors[rowId]?.lookupCode;

                if (isNewRow(original) || isRowEditing) {
                    return (
                        <div className="cell-edit">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={value}
                                disabled={isSaving}
                                placeholder={label.LookUpTableLabels.LOOKUP_CODE}
                                autoFocus={shouldAutoFocus}
                                onFocus={() => setLastClickedField("lookupCode")}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    updateDraft(rowId, "lookupCode", v);
                                    if (isNewRow(original)) {
                                        onFieldChange(rowId, "lookupCode", v);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") setDisableSaveButton(false);
                                    if (e.key === "Escape") {
                                        setLastClickedField(null);
                                        cancelEdit(rowId);
                                    }
                                }}
                            />
                            {fieldError && <small className="text-danger">{fieldError}</small>}
                        </div>
                    );
                }

                return (
                    <span
                        className="cell-readonly"
                        title="Hover or click to edit"
                        role="button"
                        tabIndex={0}
                        onMouseDown={() => enterEditRow(rowId, "lookupCode")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") enterEditRow(rowId, "lookupCode");
                        }}
                    >
                        {value}
                    </span>
                );
            },
            width: 300,
        },
        {
            Header: label.LookUpTableLabels.IN_ACTIVE,
            accessor: "inactive",
            Cell: (cell: { row?: { original?: LookUpDetailModel } }) => {
                const original = cell.row?.original;
                if (!original) return null;

                const rowId = original.id;
                const isRowEditing = editingRowId === rowId;
                const isSaving = savingRows.has(rowId);
                const isInactive = (draftRows[rowId]?.inactive ?? original.inactive ?? false) as boolean;

                if (isNewRow(original) || isRowEditing) {
                    return (
                        <label className="d-inline-flex align-items-center gap-1">
                            <input
                                type="checkbox"
                                checked={isInactive}
                                disabled={isSaving}
                                onChange={(e) => {
                                    updateDraft(rowId, "inactive", e.target.checked);
                                    if (isNewRow(original)) {
                                        onFieldChange(rowId, "inactive", e.target.checked);
                                    }
                                }}
                            />
                        </label>
                    );
                }
                return <input type="checkbox" checked={!!original.inactive} disabled aria-label="Inactive" />;
            },
            width: 120,
        },
    ];

    return (
        <>
            <Row>
                <Column lg={8} className=''>
                    <div className='grid-container card table-container-agency'>
                        <ErrorBoundary FallbackComponent={FallbackComponent}>
                            <Row>
                                <Column>
                                    {tableId ?
                                        <Label text={` ${tableName.toLocaleUpperCase()}`} className='role-name-label'></Label>
                                        : null}
                                </Column>
                            </Row>
                            <Grid
                                ref={divToScroll}
                                data={Array.isArray(gridData) ? gridData : []}
                                columns={Array.isArray(columns) ? columns : []}
                                id={'LookUpDetailsDataGrid'}
                                hiddenColumns={["selection"]}
                                disableSorting={true}
                                paginationDisabled={true}
                                showFilters={false}
                            />
                        </ErrorBoundary>
                    </div>
                </Column>
                <Column lg={1} className=''>
                    <div className='d-flex justify-content-between align-items-center mb-1'>
                        <div className='add-container'>{
                            <Button
                                disabled={disableAddButton}
                                onClick={addNewRow}
                                className='grid-icon'
                                size='lg'>
                                <Icon icon="add" className="me-1" />
                            </Button>}
                        </div>
                        <div className='add-container'>{
                            <Button
                                style={{ marginLeft: "5px" }}
                                disabled={disableSaveButton}
                                onClick={saveRecord}
                                className='grid-icon'
                                size='lg'>
                                <Icon icon="save" className="me-1" />
                            </Button>}
                        </div>
                    </div>
                </Column>
            </Row>
        </>
    );
}

export default LookUpDetails;